import type { ComponentPropsWithoutRef } from 'react';
import { createContext, useContext } from 'react';

import { classNames } from '@/utils/classNames';

import type { DropdownProps } from '@/components/base/Dropdown';
import Dropdown from '@/components/base/Dropdown';

export const Variants = {
    primary: 'bg-icoBlue text-white hover:bg-icoBlue-400',
    secondary:
        'bg-icoBlue-100 dark:bg-icoBlue/20 text-icoBlue focus:ring-2 focus:ring-icoBlue-200 focus:ring-offset-2',
    error: 'bg-icoRed hover:bg-icoRed-400 dark:bg-icoRed/20 text-white'
} as const;

const DisabledVariant = {
    default:
        'disabled:bg-icoBlack/20 dark:disabled:bg-white/5 disabled:text-white/50 dark:disabled:text-icoDarkMode-wolf disabled:cursor-not-allowed',
    light: 'disabled:bg-icoGray-300 dark:disabled:bg-white/5 disabled:text-icoGray-400 dark:disabled:text-icoDarkMode-wolf disabled:cursor-not-allowed'
} as const;

const Sizes = {
    default: {
        buttonAndDropdown: 'h-12 text-sm',
        dropdown: 'min-w-[256px]',
        dropdownButton: 'w-14',
        mainButton: 'px-5'
    },
    base: {
        buttonAndDropdown: 'min-w-[256px] h-12 text-sm',
        dropdown: 'min-w-[256px]',
        dropdownButton: 'w-14',
        mainButton: 'px-5'
    },
    sm: {
        buttonAndDropdown: 'min-w-[220px] h-8 text-xs',
        dropdown: 'min-w-[220px]',
        dropdownButton: 'w-10',
        mainButton: 'px-3'
    }
} as const;

export type ButtonAndDropdownContextType = {
    variant?: keyof typeof Variants;
    disableVariant?: keyof typeof DisabledVariant;
    size?: keyof typeof Sizes;
};

const ButtonAndDropdownContext = createContext<ButtonAndDropdownContextType>(null);

export type ButtonAndDropdownProps = ComponentPropsWithoutRef<'div'> & ButtonAndDropdownContextType;

export function ButtonAndDropdown({
    className,
    variant = 'primary',
    disableVariant = 'default',
    children,
    size = 'base',
    ...restProps
}: ButtonAndDropdownProps) {
    return (
        <ButtonAndDropdownContext.Provider value={{ variant, size, disableVariant }}>
            <div
                className={classNames(
                    'flex w-max items-center divide-x divide-white/40 rounded-md dark:divide-white/20',
                    Sizes?.[size]?.buttonAndDropdown ?? Sizes.base.buttonAndDropdown,
                    className
                )}
                {...restProps}>
                {children}
            </div>
        </ButtonAndDropdownContext.Provider>
    );
}

type MainButtonProps = ComponentPropsWithoutRef<'button'>;

function MainButton({ children, className, ...restProps }: MainButtonProps) {
    const { variant, disableVariant, size } = useContext(ButtonAndDropdownContext);
    return (
        <button
            data-testid="main-button"
            className={classNames(
                'flex h-full flex-1 items-center justify-center rounded-l-md rounded-r-none focus:ring-0 focus:ring-offset-0',
                Variants[variant],
                DisabledVariant[disableVariant ?? 'default'],
                Sizes?.[size]?.mainButton ?? Sizes.base.mainButton,
                className
            )}
            {...restProps}>
            {children}
        </button>
    );
}

ButtonAndDropdown.MainButton = MainButton;

type DropdownWrapperProps = ComponentPropsWithoutRef<'div'> & {
    button: React.ReactElement;
    dropdownPosition?: DropdownProps['position'];
};

function DropdownWrapper({ children, button, dropdownPosition = 'base' }: DropdownWrapperProps) {
    const { size } = useContext(ButtonAndDropdownContext);

    return (
        <Dropdown
            classNameWrapper="h-full"
            position={dropdownPosition}
            size="custom"
            className={Sizes?.[size]?.dropdown ?? Sizes.base.dropdown}
            button={button}>
            {children}
        </Dropdown>
    );
}

ButtonAndDropdown.DropdownWrapper = DropdownWrapper;

function DropdownButton({ children, className, ...restProps }: ComponentPropsWithoutRef<'button'>) {
    const { variant, disableVariant, size } = useContext(ButtonAndDropdownContext);
    return (
        <Dropdown.Button
            data-testid="dropdown-button"
            className={classNames(
                'flex h-full items-center justify-center rounded-l-none rounded-r-md focus:ring-0 focus:ring-offset-0',
                Variants[variant],
                DisabledVariant[disableVariant],
                Sizes?.[size]?.dropdownButton ?? Sizes.base.dropdownButton,
                className
            )}
            {...restProps}>
            {children}
        </Dropdown.Button>
    );
}

ButtonAndDropdown.DropdownButton = DropdownButton;

type DropdownItemProps = Omit<React.ComponentProps<typeof Dropdown.Item>, 'children'> & {
    children: (props: { active: boolean; disabled: boolean }) => React.ReactElement;
};

function DropdownItem({ id, disabled, children, ...restProps }: DropdownItemProps) {
    return (
        <Dropdown.Item key={id} disabled={disabled} {...restProps}>
            {({ active, disabled }: { active: boolean; disabled: boolean }) =>
                children({ active, disabled })
            }
        </Dropdown.Item>
    );
}

ButtonAndDropdown.DropdownItem = DropdownItem;
